import React from 'react';
import { ErrorBoundary } from '@/components/error/ErrorBoundary';
import { useErrorTracking } from '@/hooks/useErrorTracking';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  routeName: string;
  preserveProgress?: boolean;
}

export function RouteErrorBoundary({
  children,
  routeName,
  preserveProgress = false
}: RouteErrorBoundaryProps) {
  const { captureError } = useErrorTracking();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    console.error(`[RouteErrorBoundary:${routeName}] Error caught:`, error.message, error.stack);
    console.error(`[RouteErrorBoundary:${routeName}] Component stack:`, errorInfo.componentStack);
    captureError(error, {
      user: user ? {
        id: user.id,
        email: user.email
      } : undefined,
      extra: {
        errorInfo,
        routeName,
        preserveProgress,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      },
      tags: {
        component: 'RouteErrorBoundary',
        route: routeName,
        level: 'route'
      }
    });
  };

  const RouteErrorFallback = () => (
    <div className="flex items-center justify-center min-h-[60vh] p-6">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-xl">Something went wrong</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            We encountered an error while loading this page.
            {preserveProgress && " Don't worry - your progress has been saved and you can continue where you left off."}
          </p>
          
          <div className="flex flex-col gap-3">
            <Button 
              onClick={() => window.location.reload()} 
              className="w-full"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
            
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')}
              className="w-full"
            >
              <Home className="mr-2 h-4 w-4" />
              Go to Dashboard
            </Button>
          </div>
          
          <div className="pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground">
              If this problem persists, please contact support and mention error ID: {routeName}-{Date.now()}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <ErrorBoundary
      level="page"
      fallback={<RouteErrorFallback />}
      onError={handleError}
      enableRetry={true}
      showContactSupport={true}
    >
      {children}
    </ErrorBoundary>
  );
}